import { WordRepository as IWordRepository } from '../types/ServiceTypes';
import { WordPack } from '../types/GameTypes';
import { WORD_LISTS } from '../data';
import { config } from '../config';
import { getWordPack, getAvailableWordPacks } from '../data/wordpacks';

/**
 * WordRepository - Word list management repository
 * Handles word data access and daily word selection
 */
export class WordRepository implements IWordRepository {
  /**
   * Get all words for a specific length
   */
  getWordsForLength(length: number): string[] {
    const words = WORD_LISTS[length];
    if (!words) {
      throw new Error(`No words available for length ${length}`);
    }
    return [...words]; // Return a copy to prevent mutation
  }

  /**
   * Get a random word for a specific length
   */
  getRandomWordForLength(length: number): string {
    const words = this.getWordsForLength(length);
    if (words.length === 0) {
      throw new Error(`No words available for length ${length}`);
    }
    const randomIndex = Math.floor(Math.random() * words.length);
    return words[randomIndex];
  }

    /**
   * Get a deterministic word for a specific length using a seed
   */
  getDeterministicWordForLength(length: number, seed: number): string {
    const words = this.getWordsForLength(length);
    const index = seed % words.length;
    return words[index];
  }

  /**
   * Get the daily word for a specific length (daily challenge mode)
   * This replicates the original App.tsx getDailyWord() function behavior EXACTLY
   */
  getDailyWord(length: number): string {
    const words = this.getWordsForLength(length);
    
    // Calculate days since reference date (matches original logic EXACTLY)
    const refDate = new Date(config.dailyRefDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Match original: set to midnight
    
    const daysSinceRef = Math.floor((today.getTime() - refDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Use Math.abs like original to handle negative days
    const index = Math.abs(daysSinceRef) % words.length;
    return words[index];
  }

  /**
   * Check if a word exists in the word list for its length
   * This replicates the original validation logic exactly
   */
  isValidWord(word: string): boolean {
    const upperWord = word.toUpperCase();
    const length = upperWord.length;
    
    // Check length bounds (5-10) like original
    if (length < 5 || length > 10) {
      return false;
    }
    
    const words = WORD_LISTS[length];
    if (!words) {
      return false;
    }
    return words.includes(upperWord);
  }

  /**
   * Get available word lengths
   */
  getAvailableLengths(): number[] {
    return Object.keys(WORD_LISTS).map(Number).sort((a, b) => a - b);
  }

  /**
   * Get word count for a specific length
   */
  getWordCountForLength(length: number): number {
    const words = WORD_LISTS[length];
    return words ? words.length : 0;
  }

  // ===== WORD PACK SUPPORT (FREE PLAY MODE ONLY) =====
  // Daily mode continues to use WORD_LISTS methods above

  /**
   * Get a word pack by ID
   * Used for free play mode word pack selection
   */
  getWordPackById(packId: string): WordPack {
    const pack = getWordPack(packId);
    
    if (!pack) {
      throw new Error(`Word pack not found: ${packId}`);
    }
    
    return pack;
  }

  /**
   * Check if a word pack ID is valid
   * Used for free play mode validation
   */
  isValidWordPack(packId: string): boolean {
    try {
      const packs = getAvailableWordPacks();
      return packId in packs;
    } catch (error) {
      console.error('Error checking word pack validity:', error);
      return false;
    }
  }

  /**
   * Get a random word from a word pack
   * Used for free play mode only - daily mode uses getDailyWord()
   */
  getRandomWordFromPack(packId: string): string {
    const pack = this.getWordPackById(packId);
    
    if (!pack || !pack.words || pack.words.length === 0) {
      throw new Error(`No words available in word pack: ${packId}`);
    }
    
    const randomIndex = Math.floor(Math.random() * pack.words.length);
    return pack.words[randomIndex];
  }
}
